import React, { Component } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { accountsApi } from "../../config/config";
import TopNav from "../../components/common/TopNav";
import SideBar from "../../components/common/SideBar";

// Dashboard page to create accounts using the EXISTING registration endpoints
// (the same ones the Android app posts to; mounted here at /api/androidapi/*).
// Field names + meanings mirror the LIVE app payloads exactly:
//
// PATIENT → POST /api/androidapi/registerpatient
//   UI collects:  Name, Password, Email, Gender, Phone, Doctor, Age, Symptoms,
//                 Pacemaker/ICD, IP/OP, Monitoring days, Hospital
//   Renamed on the wire:
//     Age       → dob        (dd-MM-yyyy; year = thisYear − age, day/month = today)
//     Symptoms  → indication ("NA" if blank)
//     Pacemaker → hyperl     (the "hyperl" field carries Pacemaker/ICD, NOT lipids)
//   Blank-defaults: ipop / hospital_name → "NA"
//   Hardcoded constants the app always sends (NOT real clinical data — never
//   shown in the UI): height/weight=150, diabetes/thyroid/drughistory="Yes",
//   hypert/af/smoke/cardiac_surgery=false.
//
// DOCTOR → POST /api/androidapi/doctorRegister
//   UI "Hospital Name" → address.  gender hardcoded "F".  database_id = the
//   hospital this doctor belongs to. (password2/type are sent for app parity;
//   the endpoint ignores them.)
const PATIENT_CONSTANTS = {
  height: "150",
  weight: "150",
  diabetes: "Yes",
  thyroid: "Yes",
  drughistory: "Yes",
  hypert: false,
  af: false,
  smoke: false,
  cardiac_surgery: false,
};

const blankDoctor = {
  name: "", email: "", phone: "", password: "", hospitalName: "", database_id: "",
};
// Hospital + Customer go through the unified /api/users/AddCustomer endpoint,
// which maps a `type` string → number (customer→7, hospital→6, doctor→3,
// verifier→2) and auto-sets database_id. Same field set for both.
const blankHospital = {
  name: "", email: "", phone: "", password: "", address: "", gender: "M",
};
const blankCustomer = {
  name: "", email: "", phone: "", password: "", address: "", gender: "M",
};
const blankPatient = {
  name: "", password: "", email: "", gender: "M", phone: "",
  doctorid: "", age: "", symptoms: "", pacemaker: "no",
  ipop: "", exp_device_use_days: "30", hospital_name: "",
};

// Age → dob string "dd-MM-yyyy" (year = currentYear − age, day/month = today),
// matching how the app derives dob from the Age field.
function ageToDob(age) {
  const a = parseInt(age, 10);
  if (isNaN(a) || a < 0 || a > 150) return "";
  const now = new Date();
  const dd = String(now.getDate()).padStart(2, "0");
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const yyyy = now.getFullYear() - a;
  return `${dd}-${mm}-${yyyy}`;
}

class AddAccounts extends Component {
  constructor() {
    super();
    this.state = {
      tab: "patient",
      patient: { ...blankPatient },
      doctor: { ...blankDoctor },
      hospital: { ...blankHospital },
      customer: { ...blankCustomer },
      doctors: [],
      busy: false,
    };
  }

  componentDidMount() {
    if (localStorage.jwtToken === undefined) {
      window.location.href = "/";
      return;
    }
    this.loadDoctors();
  }

  loadDoctors = () => {
    axios
      .get(accountsApi + "api/androidapi/alldoctors")
      .then((res) => this.setState({ doctors: Array.isArray(res.data) ? res.data : [] }))
      .catch(() => this.setState({ doctors: [] }));
  };

  setField = (section, key, value) => {
    this.setState((prev) => ({ [section]: { ...prev[section], [key]: value } }));
  };

  errMsg = (err, label) => {
    if (err.response) {
      return (
        (err.response.data && (err.response.data.msg || err.response.data.error)) ||
        `${label} request failed (HTTP ${err.response.status})`
      );
    }
    return "Server unreachable — " + (err.message || "network error");
  };

  // ── PATIENT → registerpatient ────────────────────────────────────────
  submitPatient = async () => {
    const p = this.state.patient;
    if (!p.name.trim()) return toast.error("Patient name is required");
    if (!p.doctorid) return toast.error("Please select a doctor");
    if (!p.phone.trim()) return toast.error("Phone is required");
    if (!p.password || p.password.length < 4)
      return toast.error("Password (min 4 chars) is required");

    this.setState({ busy: true });
    try {
      const check = await axios.post(accountsApi + "api/androidapi/checkusername", {
        email: p.email || "",
        phone: p.phone,
      });
      if (check.data && check.data.msg && check.data.msg !== "Success") {
        this.setState({ busy: false });
        return toast.error(check.data.msg);
      }

      const body = {
        // real, user-entered fields
        name: p.name.trim(),
        password: p.password,
        email: p.email || "",
        gender: p.gender || "",
        phone: p.phone,
        doctorid: p.doctorid,
        dob: ageToDob(p.age),                       // Age → dob
        indication: (p.symptoms || "").trim() || "NA", // Symptoms → indication
        hyperl: p.pacemaker === "yes",              // Pacemaker/ICD → hyperl (boolean)
        ipop: (p.ipop || "").trim() || "NA",
        exp_device_use_days: p.exp_device_use_days || 30,
        hospital_name: (p.hospital_name || "").trim() || "NA",
        // hardcoded constants the app always sends (not real data)
        ...PATIENT_CONSTANTS,
      };

      const res = await axios.post(accountsApi + "api/androidapi/registerpatient", body);
      this.setState({ busy: false });
      const msg = (res.data && res.data.msg) || "";
      if (msg === "Success") {
        toast.success("Patient created successfully");
        this.setState({ patient: { ...blankPatient } });
      } else if (msg === "Phone Exist") {
        toast.error("A patient with this phone already exists");
      } else {
        toast.error(msg || "Patient could not be created");
      }
    } catch (err) {
      this.setState({ busy: false });
      toast.error(this.errMsg(err, "Patient"));
    }
  };

  // ── DOCTOR → doctorRegister ──────────────────────────────────────────
  submitDoctor = async () => {
    const d = this.state.doctor;
    if (!d.name.trim()) return toast.error("Doctor name is required");
    if (!d.phone.trim()) return toast.error("Phone is required");
    if (!d.password || d.password.length < 4)
      return toast.error("Password (min 4 chars) is required");

    this.setState({ busy: true });
    try {
      const check = await axios.post(accountsApi + "api/androidapi/checkDoctorUsername", {
        email: d.email || "",
        phone: d.phone,
      });
      if (check.data && check.data.msg && check.data.msg !== "Success") {
        this.setState({ busy: false });
        return toast.error(check.data.msg);
      }

      const res = await axios.post(accountsApi + "api/androidapi/doctorRegister", {
        name: d.name.trim(),
        password: d.password,
        password2: d.password,          // app parity (ignored by endpoint)
        email: d.email || "",
        phone: d.phone,
        address: (d.hospitalName || "").trim(), // Hospital Name → address
        gender: "F",                    // hardcoded on the doctor form
        type: 3,                        // doctor type (the endpoint forces 3 anyway;
                                        // 3 is safe even if a live build honors it)
        database_id: (d.database_id || "").trim(), // hospital this doctor belongs to
      });

      this.setState({ busy: false });
      const msg = (res.data && res.data.msg) || "";
      if (msg === "Success") {
        toast.success("Doctor created successfully");
        this.setState({ doctor: { ...blankDoctor } });
        this.loadDoctors();
      } else {
        toast.error(msg || "Doctor could not be created");
      }
    } catch (err) {
      this.setState({ busy: false });
      toast.error(this.errMsg(err, "Doctor"));
    }
  };

  // ── HOSPITAL / CUSTOMER → /api/users/AddCustomer ─────────────────────
  // One endpoint creates both: it maps the `type` string to the right User
  // type (hospital→6, customer→7) and sets database_id = the new _id.
  submitViaAddCustomer = (section, type, label) => async () => {
    const f = this.state[section];
    const blank = section === "customer" ? blankCustomer : blankHospital;
    if (!f.name.trim()) return toast.error(label + " name is required");
    if (!f.phone.trim()) return toast.error("Phone is required");
    if (!f.email.trim()) return toast.error("Email is required");
    if (!f.password || f.password.length < 4)
      return toast.error("Password (min 4 chars) is required");
    if (!f.address.trim()) return toast.error("Address is required");

    this.setState({ busy: true });
    try {
      const res = await axios.post(accountsApi + "api/users/AddCustomer", {
        name: f.name.trim(),
        phone: f.phone,
        email: f.email,
        password: f.password,
        address: f.address.trim(),
        gender: f.gender || "M",
        type: type, // "hospital" | "customer" — backend maps to the number
      });

      this.setState({ busy: false });
      if (res.data && res.data.success) {
        toast.success(label + " created successfully");
        this.setState({ [section]: { ...blank } });
      } else {
        toast.error((res.data && res.data.error) || (label + " could not be created"));
      }
    } catch (err) {
      this.setState({ busy: false });
      toast.error(this.errMsg(err, label));
    }
  };

  render() {
    const { tab, patient, doctor, hospital, customer, doctors, busy } = this.state;

    // Partner (distributor, verifiertype 7) reaches this page from the Partner
    // dashboard — show the partner's own left-pane menu + overlay sidebar, and
    // offer only Doctor / Hospital / Patient (no Customer tab).
    const isPartner = (localStorage.getItem("verifiertype") || "") === "7";
    const menus = isPartner
      ? [
          { title: "Patient List", url: "/Partner" },
          { title: "Device Details", url: "/device-home" },
          { title: "Add Accounts", url: "/add-accounts" },
        ]
      : [
          { title: "Verifier Home", url: "/verifier-home" },
          { title: "Patient list", url: "/patient-list" },
          { title: "Add Accounts", url: "/add-accounts" },
          { title: "Verified", url: "/verification-completed" },
        ];
    const tabList = isPartner
      ? [["patient", "Patient"], ["doctor", "Doctor"], ["hospital", "Hospital"]]
      : [["patient", "Patient"], ["doctor", "Doctor"], ["hospital", "Hospital"], ["customer", "Customer"]];

    return (
      <div>
        <Toaster position="top-right" />
        <TopNav profileLink={"verifier-profile"} />
        <SideBar menus={menus} stickyLayout={isPartner} topOffset={0} />

        <div style={{ ...styles.wrap, marginLeft: isPartner ? 24 : 240 }}>
          <div style={styles.card}>
            <h3 style={styles.title}>Add Account</h3>

            <div style={styles.tabs}>
              {tabList.map(([key, lbl]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => this.setState({ tab: key })}
                  style={{ ...styles.tab, ...(tab === key ? styles.tabActive : {}) }}
                >
                  {lbl}
                </button>
              ))}
            </div>

            {/* PATIENT */}
            {tab === "patient" && (
              <div style={styles.grid}>
                {this.text("patient", "name", "Name *", patient.name)}
                <div style={styles.field}>
                  <label style={styles.label}>Doctor *</label>
                  <select
                    value={patient.doctorid}
                    onChange={(e) => this.setField("patient", "doctorid", e.target.value)}
                    style={styles.input}
                  >
                    <option value="">— Select doctor —</option>
                    {doctors.map((d) => (
                      <option key={d._id} value={d._id}>{d.name}</option>
                    ))}
                  </select>
                </div>
                {this.text("patient", "phone", "Phone *", patient.phone)}
                {this.text("patient", "email", "Email", patient.email, "email")}
                {this.password("patient", "password", "Password *", patient.password)}
                {this.select("patient", "gender", "Gender", patient.gender, [["M", "Male"], ["F", "Female"]])}
                {this.text("patient", "age", "Age", patient.age, "number")}
                {this.select("patient", "pacemaker", "Pacemaker / ICD", patient.pacemaker, [["no", "No"], ["yes", "Yes"]])}
                {this.text("patient", "symptoms", "Symptoms", patient.symptoms)}
                {this.text("patient", "ipop", "IP/OP Number", patient.ipop)}
                {this.text("patient", "exp_device_use_days", "Monitoring Duration (days)", patient.exp_device_use_days, "number")}
                {this.text("patient", "hospital_name", "Hospital", patient.hospital_name)}
                <div style={styles.actions}>
                  <button disabled={busy} onClick={this.submitPatient} style={styles.submit}>
                    {busy ? "Saving…" : "Create Patient"}
                  </button>
                </div>
              </div>
            )}

            {/* DOCTOR */}
            {tab === "doctor" && (
              <div style={styles.grid}>
                {this.text("doctor", "name", "Name *", doctor.name)}
                {this.text("doctor", "phone", "Phone *", doctor.phone)}
                {this.text("doctor", "email", "Email", doctor.email, "email")}
                {this.password("doctor", "password", "Password *", doctor.password)}
                {this.text("doctor", "hospitalName", "Hospital Name", doctor.hospitalName)}
                {this.text("doctor", "database_id", "Hospital ID (database_id)", doctor.database_id)}
                <div style={styles.actions}>
                  <button disabled={busy} onClick={this.submitDoctor} style={styles.submit}>
                    {busy ? "Saving…" : "Create Doctor"}
                  </button>
                </div>
              </div>
            )}

            {/* HOSPITAL */}
            {tab === "hospital" && (
              <div style={styles.grid}>
                {this.text("hospital", "name", "Hospital name *", hospital.name)}
                {this.text("hospital", "phone", "Phone *", hospital.phone)}
                {this.text("hospital", "email", "Email *", hospital.email, "email")}
                {this.password("hospital", "password", "Password *", hospital.password)}
                {this.text("hospital", "address", "Address *", hospital.address)}
                {this.select("hospital", "gender", "Gender", hospital.gender, [["M", "Male"], ["F", "Female"]])}
                <div style={styles.actions}>
                  <button disabled={busy} onClick={this.submitViaAddCustomer("hospital", "hospital", "Hospital")} style={styles.submit}>
                    {busy ? "Saving…" : "Create Hospital"}
                  </button>
                </div>
              </div>
            )}

            {/* CUSTOMER */}
            {tab === "customer" && (
              <div style={styles.grid}>
                {this.text("customer", "name", "Customer name *", customer.name)}
                {this.text("customer", "phone", "Phone *", customer.phone)}
                {this.text("customer", "email", "Email *", customer.email, "email")}
                {this.password("customer", "password", "Password *", customer.password)}
                {this.text("customer", "address", "Address *", customer.address)}
                {this.select("customer", "gender", "Gender", customer.gender, [["M", "Male"], ["F", "Female"]])}
                <div style={styles.actions}>
                  <button disabled={busy} onClick={this.submitViaAddCustomer("customer", "customer", "Customer")} style={styles.submit}>
                    {busy ? "Saving…" : "Create Customer"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  text = (section, key, label, value, type = "text") => (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => this.setField(section, key, e.target.value)}
        style={styles.input}
      />
    </div>
  );

  password = (section, key, label, value) => (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type="password"
        value={value}
        autoComplete="new-password"
        onChange={(e) => this.setField(section, key, e.target.value)}
        style={styles.input}
      />
    </div>
  );

  select = (section, key, label, value, opts) => (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <select
        value={value}
        onChange={(e) => this.setField(section, key, e.target.value)}
        style={styles.input}
      >
        {opts.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    </div>
  );
}

const styles = {
  wrap: { padding: "20px", marginLeft: "240px" },
  card: { background: "#fff", borderRadius: 10, padding: "18px 22px", maxWidth: 900, boxShadow: "0 2px 10px rgba(0,0,0,0.08)" },
  title: { margin: "0 0 14px", color: "#24333e", fontSize: 20, fontWeight: 700 },
  tabs: { display: "flex", gap: 6, marginBottom: 18, borderBottom: "1px solid #eee" },
  tab: { padding: "8px 18px", fontSize: 14, fontWeight: 600, background: "none", border: "none", borderBottom: "3px solid transparent", color: "#666", cursor: "pointer" },
  tabActive: { color: "#24333e", borderBottom: "3px solid #5ac8fa" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: "12px 16px" },
  field: { display: "flex", flexDirection: "column" },
  label: { fontSize: 12, color: "#555", marginBottom: 3 },
  input: { height: 34, fontSize: 13, padding: "2px 8px", border: "1px solid #ccc", borderRadius: 6, background: "#fff", color: "#222" },
  actions: { gridColumn: "1 / -1", marginTop: 8 },
  submit: { padding: "9px 26px", fontSize: 14, fontWeight: 700, background: "#27ae60", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" },
};

export default AddAccounts;
